import { sendToErrorTracker } from "../error";
import * as logger from "../../utils/logger";
import { dispatcher, DispatcherEvents } from "../dispatcher";
import constants from "../../constants";
import { DRIVE_STORAGE_PREFIX } from "../../store/storageConstants";

/**
 * Preserve reference to original localStorage before any proxy is installed
 * This ensures our application code always uses the real localStorage,
 * while add-ins use the proxied version for tracking
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const _originalLocalStorage = window.localStorage;

// Should be refactored into a static class!
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class StorageBase {
    static get(key: string) {
        try {
            return _originalLocalStorage.getItem(key);
        } catch (e) {
            logger.error(logger.Source.ERROR, `Failed to get item from localstorage. error: ${ e }`);
            return null;
        }
    }

    static set(key: string, data) {
        try {
            _originalLocalStorage.setItem(key, typeof data === "object" ? JSON.stringify(data) : data);
        } catch (e) {
            if (e.name === constants.QUOTA_EXCEEDED_ERROR || e.code === constants.QUOTA_EXCEEDED_ERROR_CODE) {
                // Trigger event to display dialog and do a full sync
                dispatcher.trigger(DispatcherEvents.LocalStorageFull);
            } else {
                sendToErrorTracker(e, "localstorage.set");
                logger.error(logger.Source.ERROR, `Failed to set item in localstorage. error: ${ e }`);
            }
        }
    }

    static remove(key) {
        try {
            _originalLocalStorage.removeItem(key);
        } catch (e) {
            logger.error(logger.Source.ERROR, `Failed to remove item from localstorage. error: ${ e }`);
        }
    }

    static keyExist(key: string, prefix = DRIVE_STORAGE_PREFIX): boolean {
        return _originalLocalStorage.getItem(`${ prefix }${ key }`) !== null;
    }

    /**
     * Gets system data (without any checks). Only use if you know what you're doing!
     * @param key The storage key
     * @param jsonParse If true, parse JSON into a JS object
     */
    static getSystemUnsafe<T>(key: string, jsonParse = false): T | string | null {
        let data = this.get(`${ DRIVE_STORAGE_PREFIX }${ key }`);
        if (key === "@active" && data === "") {
            this.remove(`${ DRIVE_STORAGE_PREFIX }${ key }`);
            sendToErrorTracker(new Error(`@active was set to empty string.`), "javascript");
        }
        if (data && jsonParse) {
            return JSON.parse(data) as T;
        }
        return data;
    }
}